import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import {
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationCircleIcon,
  LockClosedIcon,
  LockOpenIcon,
  DocumentArrowDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ChevronLeftIcon,
} from '@heroicons/react/24/outline';
import { formatCursoLabel } from '../../utils/cursoLabel';

interface Turno {
  id: number;
  nombre: string;
  dia: string;
  horaInicio: string;
  horaFin: string;
  cursoId: number;
}

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  fechaInicioCursada: string | null;
  fechaFinCursada: string | null;
  turnos: Turno[];
}

interface AlumnoAsistencia {
  asistenciaId: number;
  alumnoId: number;
  nombre: string;
  apellido: string;
  numeroAlumno: string;
  estado: 'presente' | 'ausente' | 'justificado';
  observaciones: string | null;
}

interface ClaseDetalle {
  id: number;
  fecha: string;
  estado: 'abierta' | 'cerrada';
  observaciones: string | null;
  turno: { id: number; nombre: string; dia: string; horaInicio: string; horaFin: string; curso: { id: number; nombre: string; codigo: string } };
  alumnos: AlumnoAsistencia[];
  estadisticas: { presentes: number; ausentes: number; justificados: number; total: number };
}

interface ClaseLista {
  id: number;
  fecha: string;
  estado: 'abierta' | 'cerrada';
  observaciones: string | null;
  estadisticas: { presentes: number; ausentes: number; justificados: number; total: number };
}

interface ResumenAlumno {
  alumno: { id: number; nombre: string; apellido: string; numeroAlumno: string };
  asistenciasPorClase: Record<number, string>;
  presentes: number;
  ausentes: number;
  justificados: number;
  totalClases: number;
  porcentaje: string;
}

interface ResumenTurno {
  turno: { id: number; nombre: string; dia: string; horaInicio: string; horaFin: string; curso: { id: number; nombre: string; codigo: string } };
  clases: Array<{ id: number; fecha: string; estado: string }>;
  alumnos: ResumenAlumno[];
  totalClases: number;
}

type Tab = 'tomar' | 'historial';

const DIAS_SEMANA: Record<string, string> = {
  lunes: 'Lunes', martes: 'Martes', miercoles: 'Miércoles', miércoles: 'Miércoles',
  jueves: 'Jueves', viernes: 'Viernes', sabado: 'Sábado', sábado: 'Sábado', domingo: 'Domingo',
};

const formatFecha = (fecha: string) =>
  new Date(fecha + 'T00:00:00').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });

const AsistenciaPage: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<Tab>('tomar');
  const [selectedCursoId, setSelectedCursoId] = useState<number | null>(null);
  const [selectedTurnoId, setSelectedTurnoId] = useState<number | null>(null);
  const [selectedClaseId, setSelectedClaseId] = useState<number | null>(null);
  const [fechaApertura, setFechaApertura] = useState(new Date().toISOString().split('T')[0]);
  const [searchTerm, setSearchTerm] = useState('');

  // Local state para los cambios de asistencia (antes de guardar)
  const [estadosLocales, setEstadosLocales] = useState<Record<number, 'presente' | 'ausente' | 'justificado'>>({});

  // ─── Queries ──────────────────────────────────────────────────────────────

  const { data: cursos, isLoading: loadingCursos } = useQuery({
    queryKey: ['cursos-asistencia'],
    queryFn: async () => {
      const response = await axios.get('/cursos');
      return Array.isArray(response.data) ? response.data : [];
    },
  });

  const cursoSeleccionado = useMemo(
    () => (cursos as Curso[] | undefined)?.find(c => c.id === selectedCursoId) || null,
    [cursos, selectedCursoId],
  );

  const turnoSeleccionado = useMemo(
    () => cursoSeleccionado?.turnos?.find(t => t.id === selectedTurnoId) || null,
    [cursoSeleccionado, selectedTurnoId],
  );

  const { data: claseDetalle, isLoading: loadingClase } = useQuery<ClaseDetalle>({
    queryKey: ['clase-detalle', selectedClaseId],
    queryFn: async () => {
      const res = await axios.get(`/asistencias/clase/${selectedClaseId}`);
      return res.data;
    },
    enabled: !!selectedClaseId,
  });

  const { data: clasesList, isLoading: loadingClasesList } = useQuery<ClaseLista[]>({
    queryKey: ['clases-turno', selectedTurnoId],
    queryFn: async () => {
      const res = await axios.get(`/asistencias/turno/${selectedTurnoId}/clases`);
      return res.data;
    },
    enabled: !!selectedTurnoId && tab === 'historial',
  });

  const { data: resumen, isLoading: loadingResumen } = useQuery<ResumenTurno>({
    queryKey: ['resumen-turno', selectedTurnoId],
    queryFn: async () => {
      const res = await axios.get(`/asistencias/turno/${selectedTurnoId}/resumen`);
      return res.data;
    },
    enabled: !!selectedTurnoId && tab === 'historial',
  });

  // ─── Mutations ────────────────────────────────────────────────────────────

  const abrirClaseMutation = useMutation({
    mutationFn: async () => {
      const res = await axios.post(
        `/asistencias/turno/${selectedTurnoId}/abrir-clase?usuarioId=${user?.id || ''}`,
        { fecha: fechaApertura },
      );
      return res.data;
    },
    onSuccess: (data) => {
      setSelectedClaseId(data.id);
      setEstadosLocales({});
      queryClient.invalidateQueries({ queryKey: ['clases-turno', selectedTurnoId] });
      queryClient.invalidateQueries({ queryKey: ['clase-detalle', data.id] });
    },
    onError: () => showError('Error', 'No se pudo abrir la clase'),
  });

  const actualizarUnaMutation = useMutation({
    mutationFn: async ({ asistenciaId, estado }: { asistenciaId: number; estado: 'presente' | 'ausente' | 'justificado' }) => {
      await axios.patch(
        `/asistencias/${asistenciaId}?usuarioId=${user?.id || ''}`,
        { estado },
      );
    },
    onError: () => showError('Error', 'No se pudo guardar. Revisá la conexión e intentá de nuevo.'),
  });

  const guardarBulkMutation = useMutation({
    mutationFn: async () => {
      const asistencias = Object.entries(estadosLocales).map(([asistenciaId, estado]) => {
        const alumno = claseDetalle?.alumnos.find(a => a.asistenciaId === +asistenciaId);
        return { alumnoId: alumno?.alumnoId, estado };
      }).filter(a => a.alumnoId);

      await axios.patch(
        `/asistencias/clase/${selectedClaseId}/registrar-bulk?usuarioId=${user?.id || ''}`,
        { asistencias },
      );
    },
    onSuccess: () => {
      showSuccess('Guardado', 'Asistencias guardadas correctamente');
      setEstadosLocales({});
      queryClient.invalidateQueries({ queryKey: ['clase-detalle', selectedClaseId] });
    },
    onError: () => showError('Error', 'No se pudo guardar las asistencias'),
  });

  const cerrarClaseMutation = useMutation({
    mutationFn: async () => {
      if (Object.keys(estadosLocales).length > 0) {
        await guardarBulkMutation.mutateAsync();
      }
      await axios.patch(`/asistencias/clase/${selectedClaseId}/cerrar`);
    },
    onSuccess: () => {
      showSuccess('Clase cerrada', 'La asistencia fue guardada y cerrada');
      queryClient.invalidateQueries({ queryKey: ['clase-detalle', selectedClaseId] });
      queryClient.invalidateQueries({ queryKey: ['clases-turno', selectedTurnoId] });
    },
    onError: () => showError('Error', 'No se pudo cerrar la clase'),
  });

  const reabrirClaseMutation = useMutation({
    mutationFn: async () => axios.patch(`/asistencias/clase/${selectedClaseId}/reabrir`),
    onSuccess: () => {
      showSuccess('Clase reabierta', 'Ya podés modificar la asistencia');
      queryClient.invalidateQueries({ queryKey: ['clase-detalle', selectedClaseId] });
    },
    onError: () => showError('Error', 'No se pudo reabrir la clase'),
  });

  // ─── Handlers ─────────────────────────────────────────────────────────────

  const handleToggleEstado = (asistenciaId: number, estadoActual: string) => {
    if (claseDetalle?.estado === 'cerrada') return;
    const ciclo: Array<'presente' | 'ausente' | 'justificado'> = ['presente', 'ausente', 'justificado'];
    const idx = ciclo.indexOf(estadoActual as any);
    const nuevoEstado = ciclo[(idx + 1) % ciclo.length];
    setEstadosLocales(prev => ({ ...prev, [asistenciaId]: nuevoEstado }));
  };

  const handleSetEstado = (asistenciaId: number, estado: 'presente' | 'ausente' | 'justificado') => {
    if (claseDetalle?.estado === 'cerrada') return;
    setEstadosLocales(prev => ({ ...prev, [asistenciaId]: estado }));
    // Guardado automático: persistir de inmediato sin esperar al botón Guardar
    actualizarUnaMutation.mutate({ asistenciaId, estado });
  };

  const getEstadoEfectivo = (alumno: AlumnoAsistencia): 'presente' | 'ausente' | 'justificado' =>
    estadosLocales[alumno.asistenciaId] ?? alumno.estado;

  const haycambiosPendientes = Object.keys(estadosLocales).length > 0;

  const alumnosFiltrados = useMemo(() => {
    if (!claseDetalle?.alumnos) return [];
    if (!searchTerm.trim()) return claseDetalle.alumnos;
    const term = searchTerm.toLowerCase();
    return claseDetalle.alumnos.filter(a =>
      a.apellido.toLowerCase().includes(term) ||
      a.nombre.toLowerCase().includes(term) ||
      a.numeroAlumno.toLowerCase().includes(term),
    );
  }, [claseDetalle?.alumnos, searchTerm]);

  const estadisticasLocales = useMemo(() => {
    if (!claseDetalle) return null;
    let presentes = 0, ausentes = 0, justificados = 0;
    claseDetalle.alumnos.forEach(a => {
      const estado = getEstadoEfectivo(a);
      if (estado === 'presente') presentes++;
      else if (estado === 'ausente') ausentes++;
      else justificados++;
    });
    return { presentes, ausentes, justificados, total: claseDetalle.alumnos.length };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claseDetalle, estadosLocales]);

  // ─── Excel export ─────────────────────────────────────────────────────────

  const handleExportExcel = () => {
    if (!resumen) return;
    const BOM = '\uFEFF';

    // Sección 1: Resumen por clase (matriz comparativa)
    const matrizHeaders = ['Fecha', 'Presentes', 'Ausentes', 'Justificados', 'Total'];
    const matrizRows = resumen.clases.map(c => {
      const presentes = resumen.alumnos.filter(a => a.asistenciasPorClase[c.id] === 'presente').length;
      const ausentes = resumen.alumnos.filter(a => a.asistenciasPorClase[c.id] === 'ausente').length;
      const justificados = resumen.alumnos.filter(a => a.asistenciasPorClase[c.id] === 'justificado').length;
      const total = presentes + ausentes + justificados;
      return [formatFecha(c.fecha), presentes, ausentes, justificados, total];
    });
    const seccionMatriz = [
      `Resumen por clase - ${resumen.turno.nombre}`,
      '',
      matrizHeaders.join('\t'),
      ...matrizRows.map(r => r.join('\t')),
      '',
      '',
    ].join('\n');

    // Sección 2: Listado alumnos con P/A/J por fecha
    const fechas = resumen.clases.map(c => formatFecha(c.fecha));
    const headers = ['Legajo', 'Apellido', 'Nombre', ...fechas, '% Asistencia'];
    const rows = resumen.alumnos.map(a => {
      const celdas = resumen.clases.map(c => {
        const val = a.asistenciasPorClase[c.id] || '-';
        return val === 'presente' ? 'P' : val === 'ausente' ? 'A' : val === 'justificado' ? 'J' : '-';
      });
      return [a.alumno.numeroAlumno, a.alumno.apellido, a.alumno.nombre, ...celdas, `${a.porcentaje}%`];
    });
    const seccionAlumnos = [
      `Alumnos por clase - ${resumen.turno.nombre}`,
      '',
      headers.join('\t'),
      ...rows.map(r => r.join('\t')),
    ].join('\n');

    const content = seccionMatriz + seccionAlumnos;
    const blob = new Blob([BOM + content], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `Asistencia_${resumen.turno.nombre.replace(/\s+/g, '_')}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    showSuccess('Excel generado', 'Archivo descargado correctamente');
  };

  // ─── Render helpers ───────────────────────────────────────────────────────

  const EstadoBadge = ({ estado }: { estado: string }) => {
    if (estado === 'presente') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-800 text-xs font-medium">
        <CheckCircleIcon className="h-3.5 w-3.5" /> P
      </span>
    );
    if (estado === 'justificado') return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium">
        <ExclamationCircleIcon className="h-3.5 w-3.5" /> J
      </span>
    );
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-800 text-xs font-medium">
        <XCircleIcon className="h-3.5 w-3.5" /> A
      </span>
    );
  };

  // ─── Main render ──────────────────────────────────────────────────────────

  if (loadingCursos) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto">

      {/* Header con tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <ClipboardDocumentCheckIcon className="h-7 w-7 text-blue-600" />
            Asistencia
          </h1>
          <div className="flex bg-gray-100 rounded-lg p-1 gap-1">
            <button
              onClick={() => setTab('tomar')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                tab === 'tomar' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <ClipboardDocumentCheckIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Tomar</span>
            </button>
            <button
              onClick={() => setTab('historial')}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                tab === 'historial' ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              <ChartBarIcon className="h-4 w-4" />
              <span className="hidden sm:inline">Historial</span>
            </button>
          </div>
        </div>

        {/* Selectores de Curso y Turno */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Curso</label>
            <select
              value={selectedCursoId || ''}
              onChange={e => {
                const id = e.target.value ? +e.target.value : null;
                setSelectedCursoId(id);
                setSelectedTurnoId(null);
                setSelectedClaseId(null);
                setEstadosLocales({});
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Seleccionar curso...</option>
              {(cursos as Curso[] || []).map((c: Curso) => (
                <option key={c.id} value={c.id}>{formatCursoLabel(c)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Turno</label>
            <select
              value={selectedTurnoId || ''}
              onChange={e => {
                const id = e.target.value ? +e.target.value : null;
                setSelectedTurnoId(id);
                setSelectedClaseId(null);
                setEstadosLocales({});
              }}
              disabled={!selectedCursoId}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400"
            >
              <option value="">Seleccionar turno...</option>
              {(cursoSeleccionado?.turnos || []).map((t: Turno) => (
                <option key={t.id} value={t.id}>
                  {t.nombre} — {DIAS_SEMANA[t.dia?.toLowerCase()] || t.dia} {t.horaInicio.slice(0,5)}-{t.horaFin.slice(0,5)}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* ── TAB: TOMAR ASISTENCIA ── */}
      {tab === 'tomar' && selectedTurnoId && (
        <>
          {/* Panel de apertura de clase */}
          {!selectedClaseId && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
              <h2 className="text-base font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CalendarDaysIcon className="h-5 w-5 text-blue-600" />
                Abrir clase
              </h2>
              <div className="flex items-end gap-3">
                <div className="flex-1">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Fecha de la clase</label>
                  <input
                    type="date"
                    value={fechaApertura}
                    onChange={e => setFechaApertura(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <button
                  onClick={() => abrirClaseMutation.mutate()}
                  disabled={abrirClaseMutation.isPending}
                  className="px-5 py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {abrirClaseMutation.isPending ? 'Abriendo...' : 'Abrir clase'}
                </button>
              </div>

              {/* Clases recientes del turno */}
              <ClasesRecientes
                turnoId={selectedTurnoId}
                onSelect={id => { setSelectedClaseId(id); setEstadosLocales({}); }}
              />
            </div>
          )}

          {/* Vista de toma de asistencia */}
          {selectedClaseId && (
            <>
              {loadingClase ? (
                <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
              ) : claseDetalle ? (
                <>
                  {/* Header de la clase */}
                  <div className={`rounded-xl shadow-sm border p-4 ${claseDetalle.estado === 'cerrada' ? 'bg-gray-50 border-gray-300' : 'bg-blue-50 border-blue-200'}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <button
                        onClick={() => { setSelectedClaseId(null); setEstadosLocales({}); }}
                        className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                      >
                        <ChevronLeftIcon className="h-4 w-4" /> Volver
                      </button>
                      <div className="text-center">
                        <div className="font-bold text-gray-900">{claseDetalle.turno.nombre}</div>
                        <div className="text-sm text-gray-600">{formatFecha(claseDetalle.fecha)}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        {claseDetalle.estado === 'cerrada' ? (
                          <span className="flex items-center gap-1 text-xs font-medium text-gray-600 bg-gray-200 px-2 py-1 rounded-full">
                            <LockClosedIcon className="h-3.5 w-3.5" /> Cerrada
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-xs font-medium text-green-700 bg-green-100 px-2 py-1 rounded-full">
                            <LockOpenIcon className="h-3.5 w-3.5" /> Abierta
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Estadísticas flotantes */}
                  {estadisticasLocales && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="bg-green-50 border border-green-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-green-700">{estadisticasLocales.presentes}</div>
                        <div className="text-xs text-green-600 font-medium">Presentes</div>
                      </div>
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-red-700">{estadisticasLocales.ausentes}</div>
                        <div className="text-xs text-red-600 font-medium">Ausentes</div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 text-center">
                        <div className="text-2xl font-bold text-yellow-700">{estadisticasLocales.justificados}</div>
                        <div className="text-xs text-yellow-600 font-medium">Justificados</div>
                      </div>
                    </div>
                  )}

                  {/* Buscador */}
                  <div className="relative">
                    <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar alumno..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 bg-white"
                    />
                    {searchTerm && (
                      <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Lista de alumnos — cards touch-friendly */}
                  <div className="space-y-2">
                    {alumnosFiltrados.map(alumno => {
                      const estado = getEstadoEfectivo(alumno);
                      const esCerrada = claseDetalle.estado === 'cerrada';
                      return (
                        <div
                          key={alumno.asistenciaId}
                          className={`bg-white rounded-xl border shadow-sm p-3 transition-all ${esCerrada ? 'opacity-75' : ''}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-gray-900 text-sm truncate">
                                {alumno.apellido}, {alumno.nombre}
                              </div>
                              <div className="text-xs text-gray-500">{alumno.numeroAlumno}</div>
                            </div>

                            {/* Botones de estado */}
                            <div className="flex gap-1.5 flex-shrink-0">
                              <button
                                onClick={() => !esCerrada && handleSetEstado(alumno.asistenciaId, 'presente')}
                                disabled={esCerrada}
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                  estado === 'presente'
                                    ? 'bg-green-500 text-white shadow-md scale-105'
                                    : 'bg-gray-100 text-gray-500 hover:bg-green-100 hover:text-green-700'
                                } ${esCerrada ? 'cursor-default' : 'cursor-pointer'}`}
                              >
                                <CheckCircleIcon className="h-4 w-4" />
                                <span className="hidden sm:inline">P</span>
                              </button>
                              <button
                                onClick={() => !esCerrada && handleSetEstado(alumno.asistenciaId, 'ausente')}
                                disabled={esCerrada}
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                  estado === 'ausente'
                                    ? 'bg-red-500 text-white shadow-md scale-105'
                                    : 'bg-gray-100 text-gray-500 hover:bg-red-100 hover:text-red-700'
                                } ${esCerrada ? 'cursor-default' : 'cursor-pointer'}`}
                              >
                                <XCircleIcon className="h-4 w-4" />
                                <span className="hidden sm:inline">A</span>
                              </button>
                              <button
                                onClick={() => !esCerrada && handleSetEstado(alumno.asistenciaId, 'justificado')}
                                disabled={esCerrada}
                                className={`flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-semibold transition-all ${
                                  estado === 'justificado'
                                    ? 'bg-yellow-400 text-white shadow-md scale-105'
                                    : 'bg-gray-100 text-gray-500 hover:bg-yellow-100 hover:text-yellow-700'
                                } ${esCerrada ? 'cursor-default' : 'cursor-pointer'}`}
                              >
                                <ExclamationCircleIcon className="h-4 w-4" />
                                <span className="hidden sm:inline">J</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {alumnosFiltrados.length === 0 && (
                      <div className="text-center py-8 px-4 text-gray-500 text-sm">
                        {searchTerm ? (
                          'No se encontraron alumnos.'
                        ) : (
                          <>
                            <p className="font-medium text-gray-600">No hay alumnos inscriptos en este turno.</p>
                            <p className="mt-2 text-xs">
                              Verificá que existan inscripciones activas en este turno (Gestión de Inscripciones). 
                              Si agregaste inscripciones después de abrir la clase, tocá &quot;Actualizar lista&quot;.
                            </p>
                            <button
                              onClick={() => abrirClaseMutation.mutate()}
                              disabled={abrirClaseMutation.isPending}
                              className="mt-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200 transition-colors disabled:opacity-50"
                            >
                              {abrirClaseMutation.isPending ? 'Actualizando...' : 'Actualizar lista'}
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Barra de acción fija */}
                  {claseDetalle.estado === 'abierta' && (
                    <div className="sticky bottom-4 z-10">
                      <div className="bg-white border border-gray-200 rounded-2xl shadow-lg p-3 flex gap-2">
                        <button
                          onClick={() => guardarBulkMutation.mutate()}
                          disabled={!haycambiosPendientes || guardarBulkMutation.isPending}
                          className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-semibold text-sm hover:bg-blue-700 disabled:opacity-40 transition-colors"
                        >
                          {guardarBulkMutation.isPending ? 'Guardando...' : `Guardar${haycambiosPendientes ? ` (${Object.keys(estadosLocales).length})` : ''}`}
                        </button>
                        <button
                          onClick={() => cerrarClaseMutation.mutate()}
                          disabled={cerrarClaseMutation.isPending}
                          className="flex items-center gap-1.5 px-4 py-3 bg-gray-800 text-white rounded-xl font-semibold text-sm hover:bg-gray-900 disabled:opacity-50 transition-colors"
                        >
                          <LockClosedIcon className="h-4 w-4" />
                          Cerrar clase
                        </button>
                      </div>
                    </div>
                  )}

                  {claseDetalle.estado === 'cerrada' && (
                    <div className="flex justify-center">
                      <button
                        onClick={() => reabrirClaseMutation.mutate()}
                        disabled={reabrirClaseMutation.isPending}
                        className="flex items-center gap-2 px-5 py-2.5 border-2 border-gray-300 text-gray-700 rounded-xl font-medium text-sm hover:bg-gray-50 transition-colors"
                      >
                        <LockOpenIcon className="h-4 w-4" />
                        Reabrir clase para editar
                      </button>
                    </div>
                  )}
                </>
              ) : null}
            </>
          )}
        </>
      )}

      {/* ── TAB: HISTORIAL ── */}
      {tab === 'historial' && selectedTurnoId && (
        <>
          {(loadingClasesList || loadingResumen) ? (
            <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
          ) : resumen ? (
            <>
              {/* Resumen stats */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="font-semibold text-gray-900">
                    {resumen.turno.nombre} — {DIAS_SEMANA[resumen.turno.dia?.toLowerCase()] || resumen.turno.dia} {resumen.turno.horaInicio?.slice(0,5)}-{resumen.turno.horaFin?.slice(0,5)}
                  </h2>
                  <button
                    onClick={handleExportExcel}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                  >
                    <DocumentArrowDownIcon className="h-4 w-4" />
                    Excel
                  </button>
                </div>
                <div className="flex gap-4 text-sm text-gray-600">
                  <span><strong>{resumen.totalClases}</strong> clases dictadas</span>
                  <span><strong>{resumen.alumnos.length}</strong> alumnos</span>
                </div>
              </div>

              {/* Matriz comparativa: cantidades P/A/J por clase */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <h3 className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-200">
                  Resumen por clase
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
                        <th className="px-4 py-3 text-center font-medium text-green-600">Presentes</th>
                        <th className="px-4 py-3 text-center font-medium text-red-600">Ausentes</th>
                        <th className="px-4 py-3 text-center font-medium text-yellow-600">Justificados</th>
                        <th className="px-4 py-3 text-center font-medium text-gray-600">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {resumen.clases.map(c => {
                        const presentes = resumen.alumnos.filter(a => a.asistenciasPorClase[c.id] === 'presente').length;
                        const ausentes = resumen.alumnos.filter(a => a.asistenciasPorClase[c.id] === 'ausente').length;
                        const justificados = resumen.alumnos.filter(a => a.asistenciasPorClase[c.id] === 'justificado').length;
                        const total = presentes + ausentes + justificados;
                        return (
                          <tr key={c.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium text-gray-900">{formatFecha(c.fecha)}</td>
                            <td className="px-4 py-3 text-center text-green-600 font-semibold">{presentes}</td>
                            <td className="px-4 py-3 text-center text-red-600 font-semibold">{ausentes}</td>
                            <td className="px-4 py-3 text-center text-yellow-600 font-semibold">{justificados}</td>
                            <td className="px-4 py-3 text-center font-medium text-gray-900">{total}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Listado alumnos x clases (matriz P/A/J) */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                <h3 className="px-4 py-3 font-semibold text-gray-900 border-b border-gray-200">
                  Alumnos por clase
                </h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="px-4 py-3 text-left font-medium text-gray-600 sticky left-0 bg-gray-50 z-10 whitespace-nowrap">Alumno</th>
                        {resumen.clases.map(c => (
                          <th key={c.id} className="px-2 py-3 text-center font-medium text-gray-600 whitespace-nowrap min-w-[56px]">
                            <div>{formatFecha(c.fecha)}</div>
                            <div className={`text-xs mt-0.5 ${c.estado === 'cerrada' ? 'text-gray-400' : 'text-green-600'}`}>
                              {c.estado === 'cerrada' ? '🔒' : '🟢'}
                            </div>
                          </th>
                        ))}
                        <th className="px-3 py-3 text-center font-medium text-gray-600 sticky right-0 bg-gray-50 z-10">% Asist.</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {resumen.alumnos.map(a => (
                        <tr key={a.alumno.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 sticky left-0 bg-white z-10">
                            <div className="font-medium text-gray-900 whitespace-nowrap">{a.alumno.apellido}, {a.alumno.nombre}</div>
                            <div className="text-xs text-gray-500">{a.alumno.numeroAlumno}</div>
                          </td>
                          {resumen.clases.map(c => {
                            const val = a.asistenciasPorClase[c.id] || '-';
                            return (
                              <td key={c.id} className="px-2 py-3 text-center">
                                {val === 'presente' && <span className="inline-block w-7 h-7 rounded-full bg-green-500 text-white text-xs font-bold leading-7">P</span>}
                                {val === 'ausente' && <span className="inline-block w-7 h-7 rounded-full bg-red-400 text-white text-xs font-bold leading-7">A</span>}
                                {val === 'justificado' && <span className="inline-block w-7 h-7 rounded-full bg-yellow-400 text-white text-xs font-bold leading-7">J</span>}
                                {val === '-' && <span className="text-gray-300 text-xs">—</span>}
                              </td>
                            );
                          })}
                          <td className="px-3 py-3 text-center sticky right-0 bg-white z-10">
                            <span className={`font-bold text-sm ${
                              +a.porcentaje >= 75 ? 'text-green-600' :
                              +a.porcentaje >= 50 ? 'text-yellow-600' : 'text-red-600'
                            }`}>
                              {a.porcentaje}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {resumen.alumnos.length === 0 && (
                    <div className="text-center py-8 text-gray-500">No hay datos de asistencia para este turno.</div>
                  )}
                </div>
              </div>

              {/* Leyenda */}
              <div className="flex gap-4 text-xs text-gray-500 justify-center pb-2">
                <span className="flex items-center gap-1"><span className="inline-block w-5 h-5 rounded-full bg-green-500" /> Presente (P)</span>
                <span className="flex items-center gap-1"><span className="inline-block w-5 h-5 rounded-full bg-red-400" /> Ausente (A)</span>
                <span className="flex items-center gap-1"><span className="inline-block w-5 h-5 rounded-full bg-yellow-400" /> Justificado (J)</span>
              </div>
            </>
          ) : (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500">
              No hay clases registradas para este turno.
            </div>
          )}
        </>
      )}

      {/* Estado vacío — sin turno seleccionado */}
      {!selectedTurnoId && (
        <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
          <ClipboardDocumentCheckIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Seleccioná un curso y turno para comenzar</p>
        </div>
      )}
    </div>
  );
};

// Sub-componente: clases recientes del turno para selección rápida
const ClasesRecientes: React.FC<{ turnoId: number; onSelect: (id: number) => void }> = ({ turnoId, onSelect }) => {
  const { data: clases } = useQuery<ClaseLista[]>({
    queryKey: ['clases-turno-recientes', turnoId],
    queryFn: async () => {
      const res = await axios.get(`/asistencias/turno/${turnoId}/clases`);
      return res.data;
    },
  });

  if (!clases || clases.length === 0) return null;

  return (
    <div className="mt-5 pt-4 border-t border-gray-100">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Clases anteriores</h3>
      <div className="space-y-2">
        {clases.slice(0, 5).map(c => (
          <button
            key={c.id}
            onClick={() => onSelect(c.id)}
            className="w-full flex items-center justify-between px-3 py-2.5 bg-gray-50 hover:bg-blue-50 border border-gray-200 hover:border-blue-200 rounded-lg transition-colors text-sm"
          >
            <div className="flex items-center gap-3">
              {c.estado === 'cerrada'
                ? <LockClosedIcon className="h-4 w-4 text-gray-400" />
                : <LockOpenIcon className="h-4 w-4 text-green-500" />}
              <span className="font-medium text-gray-800">{formatFecha(c.fecha)}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <span className="text-green-600 font-medium">{c.estadisticas.presentes}P</span>
              <span className="text-red-500">{c.estadisticas.ausentes}A</span>
              {c.estadisticas.justificados > 0 && (
                <span className="text-yellow-600">{c.estadisticas.justificados}J</span>
              )}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AsistenciaPage;
